// pages/devolucionesVenta/DevolucionVenta.tsx
import React, { useContext, useEffect, useState } from 'react';
import { toast } from '../../utils/toast';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Fade,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DarkModeContext } from '../../context/darkModeContext';
import {
  devolucionVentaApi,
  IMotivoDevolucionVenta,
} from '../../services/api/devolucionVentaApi';
import { formatMoney, formatDate } from '../../utils/format';
import { DevolucionVentaList } from './DevolucionVentaList';

// ── Paleta compartida (misma que List / Modal) ──
const P = {
  light: {
    bg: '#FFFFFF', bgS: '#F7F7F5', bgT: '#F1F1EF', text: '#37352F',
    textS: '#6B6B6B', textT: '#9B9A97', border: '#E9E9E7',
    success: '#0F7B6C', successBg: '#DBEDDB',
    warning: '#D9730D', warningBg: '#FBECDD',
    error: '#E03E3E', errorBg: '#FFE2DD',
    accent: '#2383E2', accentBg: '#E8F0FE',
  },
  dark: {
    bg: '#191919', bgS: '#202020', bgT: '#2B2B2B', text: '#E8E8E6',
    textS: '#9B9A97', textT: '#6B6B6B', border: '#333333',
    success: '#4DAB9A', successBg: '#1A2F2A',
    warning: '#D9730D', warningBg: '#2F1E00',
    error: '#FF6B6B', errorBg: '#3A1C1C',
    accent: '#529CCA', accentBg: '#143A52',
  },
};

// ── Interfaces ──
interface ArticuloVenta {
  Id: number;
  Nombre: string;
  EAN?: string;
  PrecioVenta: number;
  Cantidad: number;
  CantidadDevuelta: number;
  CantidadDisponible: number;
  Descuento?: number;
  TieneDevolucionesPrevias: boolean;
}

interface VentaBuscada {
  id: number;
  codigoFactura: string;
  estadoVenta: number;
  fechaVenta: string;
  cliente?: { Nombre?: string; NumeroDocumento?: string; nombre?: string; numeroDocumento?: string };
  articulosVendidos: ArticuloVenta[];
}

interface ArticuloDevolucion {
  cantidad: number;
  idMotivo: number;
  observacion: string;
  expandido: boolean;
}

// ── Sub-componente: formulario nueva devolución ──
const NuevaDevolucionForm: React.FC<{ dark: boolean }> = ({ dark }) => {
  const p = dark ? P.dark : P.light;

  const [busquedaTipo, setBusquedaTipo] = useState<'factura' | 'cliente'>('factura');
  const [termino, setTermino] = useState('');
  const [venta, setVenta] = useState<VentaBuscada | null>(null);
  const [detalles, setDetalles] = useState<Map<number, ArticuloDevolucion>>(new Map());
  const [motivos, setMotivos] = useState<IMotivoDevolucionVenta[]>([]);
  const [motivoGlobal, setMotivoGlobal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    devolucionVentaApi.getMotivos().then((m) => {
      setMotivos(m);
      if (m.length > 0) setMotivoGlobal(m[0].Id);
    });
  }, []);

  const reset = () => {
    setVenta(null);
    setDetalles(new Map());
    setTermino('');
    setErrorMsg(null);
  };

  const handleSearch = async () => {
    if (!termino.trim()) return;
    try {
      setLoading(true);
      setErrorMsg(null);
      const res: VentaBuscada = await devolucionVentaApi.buscarVenta({
        tipo: busquedaTipo,
        termino: termino.trim(),
      });
      setVenta(res);
      const mapa = new Map<number, ArticuloDevolucion>();
      res.articulosVendidos.forEach((a) =>
        mapa.set(a.Id, {
          cantidad: 0,
          idMotivo: motivoGlobal || (motivos[0]?.Id ?? 1),
          observacion: '',
          expandido: false,
        }),
      );
      setDetalles(mapa);
    } catch (e: any) {
      const msg = e?.message ?? 'Error al buscar la venta';
      setErrorMsg(msg);
      toast.error('Error');
    } finally {
      setLoading(false);
    }
  };

  const setDetalle = (idArticulo: number, patch: Partial<ArticuloDevolucion>) => {
    setDetalles((prev) => {
      const next = new Map(prev);
      next.set(idArticulo, { ...next.get(idArticulo)!, ...patch });
      return next;
    });
  };

  // Aplicar motivo global a todos los artículos con cantidad > 0
  const aplicarMotivoGlobal = (idMotivo: number) => {
    setMotivoGlobal(idMotivo);
    setDetalles((prev) => {
      const next = new Map(prev);
      next.forEach((v, k) => next.set(k, { ...v, idMotivo }));
      return next;
    });
  };

  const articulosSeleccionados = Array.from(detalles.entries()).filter(
    ([, d]) => d.cantidad > 0,
  );

  const handleProcesar = async () => {
    if (articulosSeleccionados.length === 0) {
      toast.warning('Seleccione la cantidad a devolver para al menos un artículo.');
      return;
    }

    const sinMotivo = articulosSeleccionados.some(([, d]) => !d.idMotivo);
    if (sinMotivo) {
      toast.warning('Seleccione un motivo para cada artículo a devolver.');
      return;
    }

    try {
      setProcesando(true);
      await devolucionVentaApi.procesar({
        codigoFactura: venta!.codigoFactura,
        articulos: articulosSeleccionados.map(([idArticulo, d]) => ({
          idArticulo: Number(idArticulo),
          cantidad: d.cantidad,
          idMotivo: d.idMotivo,
          observacion: d.observacion || undefined,
        })),
      });
      toast.success('La devolución se registró correctamente.');
      reset();
    } catch (e: any) {
      toast.error('Error');
    } finally {
      setProcesando(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: p.bgS,
      borderRadius: '8px',
      color: p.text,
      '& fieldset': { borderColor: p.border },
      '&:hover fieldset': { borderColor: p.textT },
      '&.Mui-focused fieldset': { borderColor: p.accent },
    },
    '& .MuiInputLabel-root': { color: p.textT },
    '& .MuiInputLabel-root.Mui-focused': { color: p.accent },
    '& .MuiSelect-icon': { color: p.textT },
  };

  const cellSx = {
    borderBottom: `1px solid ${p.border}`,
    color: p.text,
    fontSize: '0.875rem',
    py: 1.25,
  };

  const headCellSx = {
    ...cellSx,
    color: p.textT,
    fontWeight: 600,
    fontSize: '0.72rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    bgcolor: p.bgS,
  };

  const clienteNombre =
    venta?.cliente?.Nombre ?? venta?.cliente?.nombre ?? '—';
  const clienteDoc =
    venta?.cliente?.NumeroDocumento ?? venta?.cliente?.numeroDocumento ?? '';

  return (
    <Box>
      {/* ── Panel búsqueda ── */}
      <Box
        sx={{
          p: 2.5,
          mb: 3,
          bgcolor: p.bgS,
          borderRadius: '10px',
          border: `1px solid ${p.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.72rem',
            fontWeight: 600,
            color: p.textT,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            mb: 2,
          }}
        >
          Buscar venta para devolución
        </Typography>

        <Grid container spacing={1.5} alignItems="flex-start">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" sx={inputSx}>
              <InputLabel>Buscar por</InputLabel>
              <Select
                value={busquedaTipo}
                onChange={(e) =>
                  setBusquedaTipo(e.target.value as 'factura' | 'cliente')
                }
                label="Buscar por"
              >
                <MenuItem value="factura">Número de factura</MenuItem>
                <MenuItem value="cliente">Identificación cliente</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth
              size="small"
              label={
                busquedaTipo === 'factura'
                  ? 'Número de factura'
                  : 'Identificación del cliente'
              }
              value={termino}
              onChange={(e) => setTermino(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: p.textT }} />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !termino.trim()}
              startIcon={
                loading ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <SearchIcon />
                )
              }
              sx={{
                height: 40,
                bgcolor: p.accent,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                boxShadow: 'none',
                '&:hover': { bgcolor: p.accent, opacity: 0.88, boxShadow: 'none' },
                '&.Mui-disabled': { opacity: 0.45 },
              }}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* ── Error ── */}
      {errorMsg && (
        <Fade in>
          <Alert
            severity="error"
            sx={{ mb: 2.5, borderRadius: '8px', fontSize: '0.875rem' }}
            onClose={() => setErrorMsg(null)}
          >
            {errorMsg}
          </Alert>
        </Fade>
      )}

      {/* ── Resultado ── */}
      {venta && (
        <Fade in timeout={250}>
          <Box>
            {/* Info de la venta */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Factura', value: venta.codigoFactura, mono: true },
                { label: 'Cliente', value: clienteNombre, sub: clienteDoc },
                {
                  label: 'Fecha venta',
                  value: formatDate(venta.fechaVenta),
                },
              ].map(({ label, value, sub, mono }) => (
                <Grid item xs={12} sm={4} key={label}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: p.bgS,
                      borderRadius: '8px',
                      border: `1px solid ${p.border}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: p.textT,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        mb: 0.75,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        color: p.text,
                        fontFamily: mono ? 'monospace' : undefined,
                      }}
                    >
                      {value}
                    </Typography>
                    {sub && (
                      <Typography sx={{ fontSize: '0.78rem', color: p.textS, mt: 0.25 }}>
                        {sub}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Motivo global */}
            {motivos.length > 0 && (
              <Box
                sx={{
                  mb: 2.5,
                  p: 2,
                  bgcolor: p.bgS,
                  borderRadius: '8px',
                  border: `1px solid ${p.border}`,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      color: p.textS,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Motivo (todos los artículos)
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 260, ...inputSx }}>
                    <Select
                      value={motivoGlobal}
                      onChange={(e) => aplicarMotivoGlobal(Number(e.target.value))}
                      displayEmpty
                    >
                      {motivos.map((m) => (
                        <MenuItem key={m.Id} value={m.Id}>
                          {m.Nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip title="Puedes ajustar el motivo por artículo expandiendo cada fila">
                    <InfoIcon sx={{ fontSize: 16, color: p.textT }} />
                  </Tooltip>
                </Stack>
              </Box>
            )}

            {/* Tabla artículos */}
            <Typography
              sx={{
                fontSize: '0.82rem',
                fontWeight: 600,
                color: p.textS,
                mb: 1.5,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              Artículos vendidos
              <Tooltip title="Solo se pueden devolver unidades no devueltas previamente">
                <InfoIcon sx={{ fontSize: 14, color: p.textT }} />
              </Tooltip>
            </Typography>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${p.border}`,
                borderRadius: '10px',
                bgcolor: p.bg,
                mb: 3,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Artículo</TableCell>
                    <TableCell sx={headCellSx} align="center">Original</TableCell>
                    <TableCell sx={headCellSx} align="center">Devuelto</TableCell>
                    <TableCell sx={headCellSx} align="center">Disponible</TableCell>
                    <TableCell sx={headCellSx} align="right">Precio</TableCell>
                    <TableCell sx={headCellSx} align="center">Devolver</TableCell>
                    <TableCell sx={headCellSx} align="center">Detalle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {venta.articulosVendidos.map((art) => {
                    const det = detalles.get(art.Id)!;
                    const agotado = art.CantidadDisponible === 0;

                    return (
                      <React.Fragment key={art.Id}>
                        <TableRow
                          sx={{
                            bgcolor: agotado
                              ? dark
                                ? 'rgba(255,107,107,0.04)'
                                : 'rgba(224,62,62,0.03)'
                              : 'inherit',
                            '&:hover td': { bgcolor: p.bgS },
                          }}
                        >
                          {/* Artículo */}
                          <TableCell sx={cellSx}>
                            <Stack spacing={0.25}>
                              <Typography
                                sx={{ fontWeight: 600, color: p.text, fontSize: '0.875rem' }}
                              >
                                {art.Nombre}
                              </Typography>
                              {art.EAN && (
                                <Typography
                                  sx={{
                                    fontSize: '0.72rem',
                                    color: p.textT,
                                    fontFamily: 'monospace',
                                  }}
                                >
                                  {art.EAN}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>

                          {/* Original */}
                          <TableCell sx={cellSx} align="center">
                            <Typography sx={{ color: p.textS, fontSize: '0.875rem' }}>
                              {art.Cantidad}
                            </Typography>
                          </TableCell>

                          {/* Devuelto */}
                          <TableCell sx={cellSx} align="center">
                            {art.CantidadDevuelta > 0 ? (
                              <Chip
                                size="small"
                                label={art.CantidadDevuelta}
                                icon={<DoNotDisturbOnIcon />}
                                sx={{
                                  bgcolor: dark ? P.dark.warningBg : P.light.warningBg,
                                  color: P.light.warning,
                                  fontWeight: 700,
                                  fontSize: '0.78rem',
                                  '& .MuiChip-icon': {
                                    color: P.light.warning,
                                    fontSize: 14,
                                  },
                                }}
                              />
                            ) : (
                              <Typography sx={{ color: p.textT, fontSize: '0.85rem' }}>
                                —
                              </Typography>
                            )}
                          </TableCell>

                          {/* Disponible */}
                          <TableCell sx={cellSx} align="center">
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: '5px',
                                bgcolor: agotado
                                  ? dark
                                    ? P.dark.errorBg
                                    : P.light.errorBg
                                  : dark
                                  ? P.dark.successBg
                                  : P.light.successBg,
                                color: agotado
                                  ? dark
                                    ? P.dark.error
                                    : P.light.error
                                  : dark
                                  ? P.dark.success
                                  : P.light.success,
                                fontSize: '0.8rem',
                                fontWeight: 700,
                              }}
                            >
                              {agotado ? (
                                <DoNotDisturbOnIcon sx={{ fontSize: 13 }} />
                              ) : (
                                <CheckCircleIcon sx={{ fontSize: 13 }} />
                              )}
                              {art.CantidadDisponible}
                            </Box>
                          </TableCell>

                          {/* Precio */}
                          <TableCell sx={cellSx} align="right">
                            <Typography
                              sx={{
                                fontSize: '0.85rem',
                                color: p.textS,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {formatMoney(art.PrecioVenta)}
                            </Typography>
                          </TableCell>

                          {/* Cantidad a devolver */}
                          <TableCell sx={cellSx} align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={det?.cantidad ?? 0}
                              onChange={(e) => {
                                const v = parseInt(e.target.value) || 0;
                                if (v >= 0 && v <= art.CantidadDisponible) {
                                  setDetalle(art.Id, { cantidad: v });
                                }
                              }}
                              inputProps={{
                                min: 0,
                                max: art.CantidadDisponible,
                                style: { textAlign: 'center', padding: '4px 8px' },
                              }}
                              disabled={agotado}
                              sx={{
                                width: 80,
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: p.bgS,
                                  borderRadius: '7px',
                                  color: p.text,
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  '& fieldset': { borderColor: p.border },
                                  '&:hover fieldset': { borderColor: p.textT },
                                  '&.Mui-focused fieldset': { borderColor: p.accent },
                                },
                              }}
                            />
                          </TableCell>

                          {/* Toggle detalle */}
                          <TableCell sx={cellSx} align="center">
                            <Tooltip
                              title={
                                det?.expandido
                                  ? 'Cerrar detalle'
                                  : 'Motivo y observación'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={agotado || (det?.cantidad ?? 0) === 0}
                                  onClick={() =>
                                    setDetalle(art.Id, {
                                      expandido: !det?.expandido,
                                    })
                                  }
                                  sx={{
                                    color:
                                      det?.expandido ? p.accent : p.textT,
                                    '&.Mui-disabled': { opacity: 0.3 },
                                  }}
                                >
                                  {det?.expandido ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        {/* Fila expandible: motivo + observación */}
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            sx={{ p: 0, borderBottom: det?.expandido ? `1px solid ${p.border}` : 'none' }}
                          >
                            <Collapse in={det?.expandido} unmountOnExit>
                              <Box
                                sx={{
                                  px: 3,
                                  py: 2,
                                  bgcolor: p.bgT,
                                  display: 'flex',
                                  gap: 2,
                                  flexWrap: 'wrap',
                                  alignItems: 'flex-start',
                                }}
                              >
                                {motivos.length > 0 && (
                                  <FormControl size="small" sx={{ minWidth: 220, ...inputSx }}>
                                    <InputLabel sx={{ color: p.textT }}>Motivo</InputLabel>
                                    <Select
                                      value={det?.idMotivo ?? motivoGlobal}
                                      onChange={(e) =>
                                        setDetalle(art.Id, {
                                          idMotivo: Number(e.target.value),
                                        })
                                      }
                                      label="Motivo"
                                    >
                                      {motivos.map((m) => (
                                        <MenuItem key={m.Id} value={m.Id}>
                                          {m.Nombre}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                )}

                                <TextField
                                  size="small"
                                  label="Observación (opcional)"
                                  value={det?.observacion ?? ''}
                                  onChange={(e) =>
                                    setDetalle(art.Id, {
                                      observacion: e.target.value,
                                    })
                                  }
                                  sx={{ flex: 1, minWidth: 220, ...inputSx }}
                                  inputProps={{ maxLength: 255 }}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Resumen + botón procesar */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
                p: 2,
                bgcolor: p.bgS,
                borderRadius: '10px',
                border: `1px solid ${p.border}`,
              }}
            >
              <Stack direction="row" spacing={1.5} flexWrap="wrap">
                {articulosSeleccionados.length > 0 ? (
                  articulosSeleccionados.map(([idArt, d]) => {
                    const art = venta.articulosVendidos.find((a) => a.Id === idArt);
                    return (
                      <Chip
                        key={idArt}
                        label={`${art?.Nombre ?? `#${idArt}`} × ${d.cantidad}`}
                        size="small"
                        sx={{
                          bgcolor: dark ? P.dark.accentBg : P.light.accentBg,
                          color: dark ? P.dark.accent : P.light.accent,
                          fontWeight: 600,
                          fontSize: '0.78rem',
                        }}
                      />
                    );
                  })
                ) : (
                  <Typography sx={{ color: p.textT, fontSize: '0.85rem' }}>
                    Ingrese las cantidades a devolver
                  </Typography>
                )}
              </Stack>

              <Button
                variant="contained"
                onClick={handleProcesar}
                disabled={
                  procesando || articulosSeleccionados.length === 0
                }
                startIcon={
                  procesando ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AssignmentReturnIcon />
                  )
                }
                sx={{
                  bgcolor: p.accent,
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  px: 3,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: p.accent, opacity: 0.88, boxShadow: 'none' },
                  '&.Mui-disabled': { opacity: 0.45 },
                }}
              >
                {procesando ? 'Procesando…' : 'Procesar devolución'}
              </Button>
            </Box>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

// ── Componente principal ──
const DevolucionesVenta: React.FC = () => {
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;
  const [tab, setTab] = useState(0);

  const tabSx = {
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: '0.875rem',
    color: p.textS,
    minHeight: 42,
    '&.Mui-selected': { color: p.accent },
  };

  return (
    <PageLayout icon={AssignmentReturnIcon} title="Devoluciones de Venta">
      <Box sx={{ px: 3, pt: 2, pb: 4 }}>
        {/* ── Tabs ── */}
        <Box
          sx={{
            mb: 3,
            borderBottom: `1px solid ${p.border}`,
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            TabIndicatorProps={{
              style: { backgroundColor: p.accent, height: 2, borderRadius: 2 },
            }}
            sx={{ minHeight: 42 }}
          >
            <Tab
              icon={<HistoryIcon sx={{ fontSize: 17 }} />}
              iconPosition="start"
              label="Historial"
              sx={tabSx}
            />
            <Tab
              icon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
              iconPosition="start"
              label="Nueva devolución"
              sx={tabSx}
            />
          </Tabs>
        </Box>

        {/* ── Panel Historial ── */}
        {tab === 0 && (
          <DevolucionVentaList
            onNuevaDevolucion={() => setTab(1)}
          />
        )}

        {/* ── Panel Nueva devolución ── */}
        {tab === 1 && <NuevaDevolucionForm dark={darkMode} />}
      </Box>
    </PageLayout>
  );
};

export default DevolucionesVenta;
